import { inject } from '@angular/core';
import {
  Router,
  CanActivateFn,
  CanMatchFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Route,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { AccessControlService } from '../services/auth/access-control.service';
import { FULL_ADMIN_ROLES, UserRole, isUserInRole } from '../models/role.enum';

/**
 * Privilege-based Authorization Guard (CanActivate)
 * Protects routes based on user privileges.
 * Reads required privileges from route data: { privileges: ['view_medicines', 'view_suppliers'] }
 * User needs ANY of the listed privileges to access the route.
 */
export const privilegeGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const accessControlService = inject(AccessControlService);

  if (!authService.isUserAuthenticated()) {
    router.navigate(['/auth/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  const user = authService.currentUser();
  if (user && FULL_ADMIN_ROLES.includes(user.role)) {
    return true;
  }

  const allowedRoles = route.data['allowedRoles'] as UserRole[];
  if (allowedRoles && user && isUserInRole(user.role, allowedRoles)) {
    return true;
  }

  const requiredPrivileges = route.data['privileges'] as string[];

  if (!requiredPrivileges || requiredPrivileges.length === 0) {
    return true;
  }

  if (authService.hasAnyPrivilege(requiredPrivileges)) {
    return true;
  }

  const fallback = route.data['fallbackRoute'] as string | boolean | undefined;
  if (fallback) {
    const destination = typeof fallback === 'string' ? fallback : authService.getDefaultLandingRoute();
    if (destination && destination !== state.url && destination !== '/dashboard') {
      return router.createUrlTree([destination]);
    }
    return router.createUrlTree(['/profile']);
  }

  console.warn('Access denied: User does not have required privileges');
  return accessControlService.denyTree({ privileges: requiredPrivileges, from: state.url });
};


/**
 * Factory: create a CanActivateFn guard for specific privileges.
 * Usage: canActivate: [createPrivilegeGuard(['view_medicines', 'view_suppliers'])]
 * Or with redirect fallback: canActivate: [createPrivilegeGuard(['view_dashboard'], undefined, true)]
 */
export function createPrivilegeGuard(
  requiredPrivileges: string[],
  allowedRoles?: UserRole[],
  fallbackRoute?: string | boolean
): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const accessControlService = inject(AccessControlService);

    if (!authService.isUserAuthenticated()) {
      router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    const user = authService.currentUser();
    if (user && FULL_ADMIN_ROLES.includes(user.role)) {
      return true;
    }

    if (allowedRoles && user && isUserInRole(user.role, allowedRoles)) {
      return true;
    }

    if (authService.hasAnyPrivilege(requiredPrivileges)) {
      return true;
    }

    if (fallbackRoute) {
      const destination =
        typeof fallbackRoute === 'string'
          ? fallbackRoute
          : authService.getDefaultLandingRoute?.() ?? '/profile';
      if (destination && destination !== state.url && destination !== '/dashboard') {
        return router.createUrlTree([destination]);
      }
      return router.createUrlTree(['/profile']);
    }

    console.warn('Access denied: User does not have required privileges');
    return accessControlService.denyTree({ privileges: requiredPrivileges, from: state.url });
  };
}

/**
 * Factory: create a CanActivateFn guard that requires ALL of the given privileges.
 * The page component is only created when every privilege is held; otherwise the
 * access denied page lists exactly which ones are missing.
 * Usage: canActivate: [createAllPrivilegesGuard(['create_orders', 'view_doctors'])]
 */
export function createAllPrivilegesGuard(requiredPrivileges: string[]): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const accessControlService = inject(AccessControlService);

    if (!authService.isUserAuthenticated()) {
      router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    const user = authService.currentUser();
    if (user && FULL_ADMIN_ROLES.includes(user.role)) {
      return true;
    }

    const missing = requiredPrivileges.filter((p) => !authService.hasPrivilege(p));
    if (missing.length === 0) {
      return true;
    }

    console.warn('Access denied: Missing privileges', missing);
    return accessControlService.denyTree({ privileges: missing, from: state.url });
  };
}

/**
 * Factory: create a CanMatchFn guard for specific privileges.
 * Silently skips the route (returns false) instead of showing the access denied screen.
 * Usage: canMatch: [createPrivilegeMatchGuard(['view_medicines'])]
 */
export function createPrivilegeMatchGuard(requiredPrivileges: string[]): CanMatchFn {
  return (route: Route, segments: UrlSegment[]) => {
    const authService = inject(AuthService);
    const user = authService.currentUser();
    if (user && FULL_ADMIN_ROLES.includes(user.role)) {
      return true;
    }
    return authService.hasAnyPrivilege(requiredPrivileges);
  };
}

/**
 * Factory: create a CanActivateFn guard that hard-blocks specific roles,
 * regardless of what privileges they've been granted. Use this for access
 * rules that must not be reopened by toggling a privilege in Roles settings.
 * Usage: canActivate: [createRoleBlockGuard([UserRole.PHARMACIST])]
 * Or with redirect: canActivate: [createRoleBlockGuard([UserRole.PHARMACIST], '/patient')]
 */
export function createRoleBlockGuard(
  blockedRoles: UserRole[],
  redirectUrl?: string
): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const accessControlService = inject(AccessControlService);

    if (!authService.isUserAuthenticated()) {
      return router.createUrlTree(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    const user = authService.currentUser();
    if (user && isUserInRole(user.role, blockedRoles)) {
      if (redirectUrl) {
        return router.createUrlTree([redirectUrl]);
      }
      console.warn('Access denied: Role is blocked from this route');
      return accessControlService.denyTree({ from: state.url });
    }

    return true;
  };
}
